// frontend/app/components/lessonPlans/useLessonBlockHandlers.js
//
// The answer / check / launch handlers, shared by the full lesson workspace and
// the compact companion that docks beside an open 3D scene or video. Both
// surfaces write the same session state, so a check answered next to the model
// is the same answer as one made in the workspace.

import { useCallback, useMemo } from "react";

import {
  compositeBlockId,
  recordAnswer,
  recordChecked,
  recordLaunch,
  recordQuizAnswer,
} from "./lessonPlanRules";

const noop = () => {};

/**
 * Which submissions go to the AI evaluator. Explanations and probes always do;
 * a prediction only when the author asked for it, because a prediction is
 * recorded prior knowledge and scoring one by default would punish an honest
 * guess made before the lesson.
 */
const shouldAiEvaluate = (block) => {
  switch (block?.type) {
    case "explanation":
    case "exploration_probe":
      return true;
    case "prediction":
      return block.ai_feedback === true;
    default:
      return false;
  }
};

export default function useLessonBlockHandlers({
  sessionState,
  onSessionStateChange,
  readOnly = false,
  onLaunchBlock,
  // Wired to the backend evaluate endpoint by the caller. Both default to a
  // no-op so a surface that does not care about AI scoring or the exploration
  // event stream — the authoring preview, say — needs to pass neither.
  onAiEvaluate = noop,
  onExplorationEvent = noop,
}) {
  const handleAnswer = useCallback(
    (block, value) => {
      if (readOnly || !onSessionStateChange) return;
      onSessionStateChange(recordAnswer(sessionState, block, value));
    },
    [onSessionStateChange, readOnly, sessionState]
  );

  const handleCheck = useCallback(
    (block) => {
      if (readOnly || !onSessionStateChange) return;
      onSessionStateChange(recordChecked(sessionState, block?.id));

      const entry = (sessionState && sessionState[block?.id]) || null;
      onExplorationEvent({
        type: "attempt",
        block_id: block?.id,
        block_type: block?.type,
      });
      if (shouldAiEvaluate(block)) {
        onAiEvaluate(block.id, block.type, entry?.value);
      }
    },
    [
      onAiEvaluate,
      onExplorationEvent,
      onSessionStateChange,
      readOnly,
      sessionState,
    ]
  );

  const handleLaunch = useCallback(
    (block) => {
      if (onSessionStateChange && !readOnly) {
        onSessionStateChange(recordLaunch(sessionState, block.id));
      }
      onExplorationEvent({
        type: "scene_launch",
        block_id: block?.id,
        block_type: block?.type,
      });
      if (onLaunchBlock) onLaunchBlock(block);
    },
    [
      onExplorationEvent,
      onLaunchBlock,
      onSessionStateChange,
      readOnly,
      sessionState,
    ]
  );

  /**
   * One answer to one question inside the inline topic quiz.
   *
   * Kept apart from `handleAnswer` because the two disagree on both halves of
   * the job: the answer is stored under a composite block id rather than the
   * block's own, and the grade comes from the bank question rather than from
   * `gradeAnswer`, which knows only about authored check blocks.
   */
  const handleQuizAnswer = useCallback(
    (block, { questionId, value, isCorrect = null } = {}) => {
      if (readOnly || !onSessionStateChange) return;
      if (!block?.id || questionId === null || questionId === undefined) return;

      onSessionStateChange(
        recordQuizAnswer(sessionState, block.id, questionId, { value, isCorrect })
      );
      onExplorationEvent({
        type: "attempt",
        block_id: compositeBlockId(block.id, questionId),
        block_type: block.type,
      });
    },
    [onExplorationEvent, onSessionStateChange, readOnly, sessionState]
  );

  /** Revealing a probe's hint. Evidence of how a student is working, never a
   *  penalty — the Loop counts hint requests as exploration, not as a mistake. */
  const handleHintRequest = useCallback(
    (blockId) => {
      onExplorationEvent({ type: "hint_request", block_id: blockId });
    },
    [onExplorationEvent]
  );

  return useMemo(
    () => ({
      handleAnswer,
      handleCheck,
      handleLaunch,
      handleHintRequest,
      handleQuizAnswer,
    }),
    [
      handleAnswer,
      handleCheck,
      handleLaunch,
      handleHintRequest,
      handleQuizAnswer,
    ]
  );
}
